'use client';

import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { CheckCircle, ChevronRight, Package, Search, PawPrint, Loader2 } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import AppLogo from '@/components/ui/AppLogo';

const STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to DrivenMarket',
    subtitle: 'The UK\'s specialist marketplace for working dog and sport dog equipment.',
  },
  {
    id: 'role',
    title: 'How will you use DrivenMarket?',
    subtitle: 'This helps us personalise your experience.',
  },
  {
    id: 'done',
    title: 'You\'re all set!',
    subtitle: 'Your account is ready. Here\'s what you can do next.',
  },
];

type Role = 'buyer' | 'seller' | 'both' | null;

export default function OnboardingModal({ onComplete }: { onComplete: () => void }) {
  const { user } = useAuth();
  const router = useRouter();
  const [step, setStep] = useState(0);
  const [role, setRole] = useState<Role>(null);
  const [saving, setSaving] = useState(false);

  async function handleComplete(nextPath?: string) {
    if (!user) return;
    setSaving(true);
    const supabase = createClient();
    await supabase.from('user_profiles').update({ onboarding_completed: true }).eq('id', user.id);
    setSaving(false);
    onComplete();
    if (nextPath) router.push(nextPath);
  }

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="w-full max-w-md bg-[hsl(222,40%,9%)] border border-[hsl(222,25%,18%)] rounded-2xl shadow-2xl overflow-hidden">
        {/* Progress bar */}
        <div className="h-1 bg-[hsl(222,35%,12%)]">
          <div
            className="h-full bg-amber-500 transition-all duration-500"
            style={{ width: `${((step + 1) / STEPS.length) * 100}%` }}
          />
        </div>

        <div className="p-8">
          {/* Logo */}
          <div className="flex items-center gap-2 mb-8">
            <AppLogo size={28} />
            <span className="font-display text-base text-white">Driven<span className="text-amber-400">Market</span></span>
          </div>

          {/* Step 0: Welcome */}
          {step === 0 && (
            <div>
              <div className="w-14 h-14 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mb-6">
                <PawPrint className="w-7 h-7 text-amber-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">{STEPS[0].title}</h2>
              <p className="text-sm text-slate-400 leading-relaxed mb-6">{STEPS[0].subtitle}</p>
              <ul className="space-y-3 mb-8">
                {[
                  'Buy & sell working dog equipment',
                  'Secure payments with buyer protection',
                  'Connect with the UK dog training community',
                  'Find puppies from verified breeders',
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-sm text-slate-300">
                    <CheckCircle className="w-4 h-4 text-amber-400 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => setStep(1)}
                className="w-full flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold py-3 rounded-xl text-sm transition-all duration-150 active:scale-95"
              >
                Get Started <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Step 1: Role */}
          {step === 1 && (
            <div>
              <h2 className="text-xl font-bold text-white mb-2">{STEPS[1].title}</h2>
              <p className="text-sm text-slate-400 mb-6">{STEPS[1].subtitle}</p>
              <div className="flex flex-col gap-3 mb-8">
                {[
                  { value: 'buyer' as Role, label: 'I\'m mainly buying', icon: Search, desc: 'Browse and purchase equipment' },
                  { value: 'seller' as Role, label: 'I\'m mainly selling', icon: Package, desc: 'List and sell your equipment' },
                  { value: 'both' as Role, label: 'Both buying and selling', icon: PawPrint, desc: 'Full marketplace access' },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setRole(opt.value)}
                    className={`flex items-center gap-4 p-4 rounded-xl border text-left transition-all duration-150 ${role === opt.value ? 'border-amber-500/50 bg-amber-500/8' : 'border-[hsl(222,25%,18%)] hover:border-slate-500'}`}
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${role === opt.value ? 'bg-amber-500/15' : 'bg-[hsl(222,35%,12%)]'}`}>
                      <opt.icon className={`w-5 h-5 ${role === opt.value ? 'text-amber-400' : 'text-slate-500'}`} />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-white">{opt.label}</div>
                      <div className="text-xs text-slate-500">{opt.desc}</div>
                    </div>
                    {role === opt.value && <CheckCircle className="w-4 h-4 text-amber-400 ml-auto shrink-0" />}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setStep(2)}
                disabled={!role}
                className="w-full flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-slate-900 font-semibold py-3 rounded-xl text-sm transition-all duration-150 active:scale-95"
              >
                Continue <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Step 2: Done */}
          {step === 2 && (
            <div>
              <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-6">
                <CheckCircle className="w-7 h-7 text-emerald-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">{STEPS[2].title}</h2>
              <p className="text-sm text-slate-400 mb-6">{STEPS[2].subtitle}</p>
              <div className="flex flex-col gap-3">
                {(role === 'seller' || role === 'both') && (
                  <button
                    onClick={() => handleComplete('/create-listing')}
                    disabled={saving}
                    className="w-full flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold py-3 rounded-xl text-sm transition-all duration-150 active:scale-95"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Package className="w-4 h-4" />}
                    Post Your First Listing
                  </button>
                )}
                <button
                  onClick={() => handleComplete('/listings-feed')}
                  disabled={saving}
                  className="w-full flex items-center justify-center gap-2 border border-[hsl(222,25%,18%)] hover:border-amber-500/40 text-slate-300 hover:text-white py-3 rounded-xl text-sm font-medium transition-all duration-150"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  Browse Listings
                </button>
                <button
                  onClick={() => handleComplete()}
                  disabled={saving}
                  className="text-xs text-slate-600 hover:text-slate-400 transition-colors py-2"
                >
                  Skip for now
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
